import { AnalyticsProvider, AnalyticsProviderName } from './types';
import { noopAnalytics } from './noopAnalytics';
import { posthogAnalytics } from './posthogAnalytics';
import { createNewUserWindowGate, SHARED_WINDOWED_EVENTS } from '@run-diffusion/shared';
import { EVENTS } from './events';

// Active provider. Defaults to noop until initAnalytics() promotes it to
// PostHog — so events fired before init (or when analytics is disabled)
// are silently dropped rather than throwing.
let active: AnalyticsProvider = noopAnalytics;

// New-user tracking window (ADR-0036). These four web events are the bulk of
// PostHog ingestion — board_generation_run alone was 45% of all project volume
// — and they are only ever read to answer new-user questions, so they are
// dropped once an account is past NEW_USER_EVENT_WINDOW_DAYS. Names come from
// the EVENTS map, never literals. generation_failed is deliberately absent: it
// is a reliability signal whose value is whole-population coverage.
const windowGate = createNewUserWindowGate([
  ...SHARED_WINDOWED_EVENTS,
  EVENTS.BOARD_GENERATION_RUN,
  EVENTS.IMAGE_DOWNLOADED,
  EVENTS.REMIX_INITIATED,
  EVENTS.RESULT_FAVORITED,
]);

export function initAnalytics(enabled: boolean): AnalyticsProvider {
  active = enabled ? posthogAnalytics : noopAnalytics;
  if (typeof console !== 'undefined') {
    console.log(`[analytics] active provider: ${active.name}`);
  }
  return active;
}

// Stable facade — import this anywhere and call analytics.track(...).
// Always reads through to the current active provider.
export const analytics: AnalyticsProvider = {
  get name() { return active.name; },
  track: (event, properties) => {
    if (!windowGate.allows(event)) return;
    active.track(event, properties);
  },
  identify: (distinctId, properties) => active.identify(distinctId, properties),
  register: (superProperties) => {
    // register() is where account_age_days reaches this layer on every surface,
    // so the gate reads it here rather than needing its own plumbing.
    windowGate.observeSuperProps(superProperties);
    active.register(superProperties);
  },
  reset: () => {
    windowGate.reset();
    active.reset();
  },
  isReady: () => active.isReady(),
};

export { EVENTS } from './events';
export type { AnalyticsEvent } from './events';
// Cross-surface taxonomy (shared with plugins) — see ADR-0016.
export { SHARED_EVENTS, SURFACES, SUPER_PROPS } from './events';
export type { Surface, SuperPropKey, SharedEvent } from './events';
// Identity assembly helpers (shared with plugins) — see ADR-0016.
export { buildSuperProps, buildPersonProps, accountAgeDaysFromMs } from '@run-diffusion/shared';
export type { AnalyticsProvider, AnalyticsProviderName };
